import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import { PlusCircle } from '@phosphor-icons/react'
import ActionFeedbackAlert, {
  ACTION_FEEDBACK_DURATION_MS
} from '../../../components/alerts/ActionFeedbackAlert'
import LoadingScreen from '../../../components/LoadingScreen'
import MuiFilterToolbar from '../../../components/Filters/MuiFilterToolbar'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../../components/MuiTablePagination'
import FormHeaderDetailActions from '../../../components/action_button/FormHeaderDetailActions'
import { layoutMobileMediaQuery } from '../../../layout/layoutConstants'
import {
  UI_COLOR_DANGER_ACTION,
  UI_COLOR_DANGER_ACTION_HOVER,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER
} from '../../../constants/uiColors'
import { isAuthRedirectError } from '../../../auth'
import { canManageRbacRoles } from '../../../global_context/capabilities/rbacManagementCapabilities'
import { buildSelectValue, getSelectValues } from '../../../components/Filters/filterSelectState'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import RbacRoleConfirmDialog from '../components/RbacRoleConfirmDialog'
import RbacRolesTable from '../components/RbacRolesTable'
import { fetchRoles, updateRole } from '../services/rolesApi'
import {
  RBAC_ROLE_PERMISSIONS_TAB_VALUE,
  RBAC_ROLE_USERS_TAB_VALUE,
  RBAC_ROLE_DETAIL_TEXTS
} from '../shared/rbacRoleDetailConstants'
import { RBAC_ROLE_FEEDBACK_STATE_KEY } from '../shared/rbacRoleNavigationFeedback'
import {
  getRbacRoleCannotInactivateMessage,
  isRbacRoleActive,
  roleHasAssignedUsers
} from '../shared/rbacRoleDetailHelpers'

const DEFAULT_RBAC_ROWS_PER_PAGE = 25
const ROLE_FILTER_KEYS = {
  ROLE: 'role_ids',
  STATUS: 'status'
}

export default function RbacRolesListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)
  const canCreateRole = canManageRbacRoles()

  const initialSearchInput = searchParams.get('q') || ''
  const initialRoleFilter = buildSelectValue(
    searchParams
      .get(ROLE_FILTER_KEYS.ROLE)
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) || []
  )
  const initialStatusFilter = buildSelectValue(
    searchParams
      .get(ROLE_FILTER_KEYS.STATUS)
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) || []
  )
  const initialPage = Math.max(Number(searchParams.get('page')) || 1, 1)
  const initialPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(Number(searchParams.get('per_page')))
    ? Number(searchParams.get('per_page'))
    : DEFAULT_RBAC_ROWS_PER_PAGE

  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionAlert, setActionAlert] = useState(null)
  const [pagination, setPagination] = useState({
    current_page: initialPage,
    per_page: initialPerPage,
    total_count: 0,
    total_pages: 1
  })
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null)
  const [actionMenuRole, setActionMenuRole] = useState(null)
  const [searchInput, setSearchInput] = useState(initialSearchInput)
  const [appliedSearch, setAppliedSearch] = useState(initialSearchInput)
  const [appliedFilterSelections, setAppliedFilterSelections] = useState({
    [ROLE_FILTER_KEYS.ROLE]: initialRoleFilter,
    [ROLE_FILTER_KEYS.STATUS]: initialStatusFilter
  })
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [statusTarget, setStatusTarget] = useState(null)
  const [cannotInactivateOpen, setCannotInactivateOpen] = useState(false)
  const [cannotInactivateRole, setCannotInactivateRole] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  const roleFilterConfigs = useMemo(
    () => [
      {
        key: ROLE_FILTER_KEYS.ROLE,
        title: 'ROL',
        options: roles.map((role) => ({ value: String(role.id), label: role.name || '—' }))
      },
      {
        key: ROLE_FILTER_KEYS.STATUS,
        title: 'ESTADO',
        options: [
          { value: 'active', label: 'ACTIVO' },
          { value: 'inactive', label: 'INACTIVO' }
        ]
      }
    ],
    [roles]
  )

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const rolesData = await fetchRoles({ includeInactive: true })
      setRoles(Array.isArray(rolesData) ? rolesData : [])
      setLoading(false)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(err.message)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (!actionAlert) return undefined

    const dismissTimer = window.setTimeout(() => {
      setActionAlert(null)
    }, ACTION_FEEDBACK_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [actionAlert])

  useEffect(() => {
    if (!error) return undefined

    const dismissTimer = window.setTimeout(() => {
      setError('')
    }, ACTION_FEEDBACK_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [error])

  useEffect(() => {
    const feedback = location.state?.[RBAC_ROLE_FEEDBACK_STATE_KEY]
    if (!feedback?.message) return

    const navigateToAssignUsers =
      feedback.ctaKind === 'assign-users' && feedback.roleId
        ? () => {
            navigate(`/configuracion/rbac/roles/${feedback.roleId}?tab=${RBAC_ROLE_USERS_TAB_VALUE}`, {
              state: {
                from: `${location.pathname}${location.search}`
              }
            })
          }
        : null

    setActionAlert({
      id: Date.now(),
      severity: feedback.severity || 'success',
      message: navigateToAssignUsers ? (
        <Box component="span">
          {feedback.message}{' '}
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={navigateToAssignUsers}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                navigateToAssignUsers()
              }
            }}
            sx={{
              p: 0,
              minWidth: 0,
              color: 'inherit',
              fontSize: 'inherit',
              fontWeight: 700,
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            {feedback.ctaLabel || 'Ir a asignar usuarios'}
          </Box>
        </Box>
      ) : (
        feedback.message
      )
    })

    const { [RBAC_ROLE_FEEDBACK_STATE_KEY]: _ignored, ...nextState } = location.state || {}

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null
      }
    )
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    const nextParams = new URLSearchParams()
    const trimmedSearch = appliedSearch.trim()
    const roleValues = getSelectValues(appliedFilterSelections[ROLE_FILTER_KEYS.ROLE])
    const statusValues = getSelectValues(appliedFilterSelections[ROLE_FILTER_KEYS.STATUS])

    if (trimmedSearch) nextParams.set('q', trimmedSearch)
    if (roleValues.length > 0) nextParams.set(ROLE_FILTER_KEYS.ROLE, roleValues.join(','))
    if (statusValues.length > 0) nextParams.set(ROLE_FILTER_KEYS.STATUS, statusValues.join(','))
    if (pagination.current_page > 1) nextParams.set('page', String(pagination.current_page))
    if (pagination.per_page !== DEFAULT_RBAC_ROWS_PER_PAGE) {
      nextParams.set('per_page', String(pagination.per_page))
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true })
    }
  }, [
    appliedFilterSelections,
    appliedSearch,
    pagination.current_page,
    pagination.per_page,
    searchParams,
    setSearchParams
  ])

  const filteredRoles = useMemo(() => {
    const selectedRoleIds = getSelectValues(appliedFilterSelections[ROLE_FILTER_KEYS.ROLE])
    const selectedStatuses = getSelectValues(appliedFilterSelections[ROLE_FILTER_KEYS.STATUS])
    const searchTerm = appliedSearch.trim().toLowerCase()

    return roles.filter((role) => {
      if (selectedRoleIds.length > 0 && !selectedRoleIds.includes(String(role.id))) {
        return false
      }

      if (selectedStatuses.length > 0) {
        const normalizedStatus = String(role.status || '').toLowerCase()
        if (!selectedStatuses.includes(normalizedStatus)) return false
      }

      if (!searchTerm) return true

      const haystack =
        `${role.name || ''} ${role.uid || ''} ${role.description || ''}`.toLowerCase()
      return haystack.includes(searchTerm)
    })
  }, [appliedFilterSelections, appliedSearch, roles])

  useEffect(() => {
    setPagination((prevPagination) => {
      const totalCount = filteredRoles.length
      const totalPages = Math.max(Math.ceil(totalCount / prevPagination.per_page), 1)
      const currentPage = Math.min(prevPagination.current_page, totalPages)

      if (
        prevPagination.total_count === totalCount &&
        prevPagination.total_pages === totalPages &&
        prevPagination.current_page === currentPage
      ) {
        return prevPagination
      }

      return {
        ...prevPagination,
        total_count: totalCount,
        total_pages: totalPages,
        current_page: currentPage
      }
    })
  }, [filteredRoles.length])

  const paginatedRoles = useMemo(() => {
    const startIndex = (pagination.current_page - 1) * pagination.per_page
    return filteredRoles.slice(startIndex, startIndex + pagination.per_page)
  }, [filteredRoles, pagination.current_page, pagination.per_page])

  const isAnyFilterSelected = useMemo(
    () =>
      Object.values(appliedFilterSelections).some(
        (selection) => getSelectValues(selection).length > 0
      ) || Boolean(appliedSearch.trim()),
    [appliedFilterSelections, appliedSearch]
  )

  const clearHeaderAlert = useCallback(() => {
    setError('')
    setActionAlert(null)
  }, [])

  const showActionAlert = useCallback((message, severity = 'success') => {
    setError('')
    setActionAlert({
      id: Date.now(),
      severity,
      message
    })
  }, [])

  const handleMuiPageChange = (_event, nextPage) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: nextPage
    }))
  }

  const handleFilterChange = (filterKey, nextValue) => {
    setAppliedFilterSelections((prevSelections) => ({
      ...prevSelections,
      [filterKey]: nextValue
    }))
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    setAppliedSearch(searchInput)
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleSearchClear = () => {
    setSearchInput('')
    setAppliedSearch('')
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleResetFilters = () => {
    setAppliedFilterSelections({
      [ROLE_FILTER_KEYS.ROLE]: buildSelectValue(),
      [ROLE_FILTER_KEYS.STATUS]: buildSelectValue()
    })
    setSearchInput('')
    setAppliedSearch('')
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleMuiRowsPerPageChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_RBAC_ROWS_PER_PAGE

    setPagination((prevPagination) => ({
      ...prevPagination,
      per_page: safeRowsPerPage,
      current_page: 1,
      total_pages: Math.max(Math.ceil((prevPagination.total_count || 0) / safeRowsPerPage), 1)
    }))
  }

  const handleRoleRowClick = (roleId) => {
    navigate(`/configuracion/rbac/roles/${roleId}`, {
      state: {
        from: `${location.pathname}${location.search}`
      }
    })
  }

  const openActionMenu = (event, role) => {
    event.stopPropagation()
    clearHeaderAlert()
    setActionMenuAnchorEl(event.currentTarget)
    setActionMenuRole(role)
  }

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null)
  }

  const handleActionMenuExited = () => {
    setActionMenuRole(null)
  }

  const handleEditAction = () => {
    const role = actionMenuRole
    closeActionMenu()
    if (role?.id) navigate(`/configuracion/rbac/roles/${role.id}/editar`)
  }

  const handleViewPermissionsAction = () => {
    const role = actionMenuRole
    closeActionMenu()
    if (role?.id) {
      navigate(`/configuracion/rbac/roles/${role.id}?tab=${RBAC_ROLE_PERMISSIONS_TAB_VALUE}`, {
        state: {
          from: `${location.pathname}${location.search}`
        }
      })
    }
  }

  const handleViewUsersAction = () => {
    const role = actionMenuRole
    closeActionMenu()
    if (role?.id) {
      navigate(`/configuracion/rbac/roles/${role.id}?tab=${RBAC_ROLE_USERS_TAB_VALUE}`, {
        state: {
          from: `${location.pathname}${location.search}`
        }
      })
    }
  }

  const openStatusDialog = (role) => {
    clearHeaderAlert()
    setStatusTarget(role)
    setStatusDialogOpen(true)
  }

  const openCannotInactivateDialog = (role) => {
    clearHeaderAlert()
    setCannotInactivateRole(role)
    setCannotInactivateOpen(true)
  }

  const handleStatusAction = () => {
    const role = actionMenuRole
    closeActionMenu()
    if (!role) return

    if (isRbacRoleActive(role) && roleHasAssignedUsers(role)) {
      openCannotInactivateDialog(role)
      return
    }

    openStatusDialog(role)
  }

  const closeStatusDialog = () => {
    setStatusDialogOpen(false)
  }

  const handleStatusDialogExited = () => {
    setStatusTarget(null)
  }

  const closeCannotInactivateDialog = () => {
    setCannotInactivateOpen(false)
  }

  const handleCannotInactivateDialogExited = () => {
    setCannotInactivateRole(null)
  }

  const handleGoToReassignUsers = () => {
    const role = cannotInactivateRole
    closeCannotInactivateDialog()
    if (!role?.id) return

    navigate(`/configuracion/rbac/roles/${role.id}?tab=${RBAC_ROLE_USERS_TAB_VALUE}`, {
      state: {
        from: `${location.pathname}${location.search}`
      }
    })
  }

  const handleUpdateRoleStatus = async () => {
    if (!statusTarget) return

    if (isRbacRoleActive(statusTarget) && roleHasAssignedUsers(statusTarget)) {
      const blockedRole = statusTarget
      closeStatusDialog()
      openCannotInactivateDialog(blockedRole)
      return
    }

    try {
      setUpdatingStatus(true)
      setError('')
      const willActivate = !isRbacRoleActive(statusTarget)
      const updated = await updateRole(statusTarget.id, {
        status: willActivate ? 'active' : 'inactive'
      })
      await loadData()
      showActionAlert(
        willActivate
          ? `Rol ${updated.name || statusTarget.name} activado correctamente.`
          : `Rol ${updated.name || statusTarget.name} inactivado correctamente.`
      )
      closeStatusDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      closeStatusDialog()
      setError(err.message)
    } finally {
      setUpdatingStatus(false)
    }
  }

  if (loading && roles.length === 0) {
    return <LoadingScreen text="Cargando..." />
  }

  const hasRoles = (pagination.total_count || 0) > 0 || roles.length > 0
  const filtersDisabled = !hasRoles && !isAnyFilterSelected
  const statusTargetActive = isRbacRoleActive(statusTarget)
  const headerAlert = statusDialogOpen || cannotInactivateOpen
    ? null
    : error
    ? {
        id: `roles-error-${error}`,
        severity: 'error',
        message: error
      }
    : actionAlert

  return (
    <>
      <div
        className="index-header detailed-container index-header--action-bar"
        style={{
          marginBottom: isMobileLayout ? (headerAlert ? 16 : undefined) : undefined
        }}
      >
        <h2>Roles y permisos</h2>
        {headerAlert ? (
          <div className="index-buttons create-action-btn-group">
            <ActionFeedbackAlert
              alert={headerAlert}
              onClose={() => {
                if (error) setError('')
                else setActionAlert(null)
              }}
              isMobileLayout={isMobileLayout}
              sx={{
                width: { xs: '100%', md: 'fit-content' },
                mt: 0 }}
            />
          </div>
        ) : canCreateRole ? (
          <div className="index-buttons create-action-btn-group">
            <FormHeaderDetailActions
              showSecondary={false}
              primaryLabel="Crear rol nuevo"
              primaryIcon={PlusCircle}
              iconSize={24}
              onPrimary={() => navigate('/configuracion/rbac/roles/nuevo')}
            />
          </div>
        ) : null}
      </div>

      <div className="index-table">
        <div className="show-area show-area--flush">
          <MuiFilterToolbar
            title="Filtrar roles por:"
            searchPlaceholder="Buscar rol por nombre o codigo"
            filterConfigs={roleFilterConfigs}
            appliedFilterSelections={appliedFilterSelections}
            isAnyFilterSelected={isAnyFilterSelected}
            searchInput={searchInput}
            disabled={filtersDisabled}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
            onSearchChange={handleSearchChange}
            onSearchEnter={handleSearchEnter}
            onSearchClear={handleSearchClear}
          />

          <RbacRolesTable
            loading={loading}
            filteredRolesCount={filteredRoles.length}
            paginatedRoles={paginatedRoles}
            pagination={pagination}
            actionMenuAnchorEl={actionMenuAnchorEl}
            actionMenuRole={actionMenuRole}
            onRoleRowClick={handleRoleRowClick}
            onOpenActionMenu={openActionMenu}
            onCloseActionMenu={closeActionMenu}
            onActionMenuExited={handleActionMenuExited}
            onEditAction={handleEditAction}
            onViewPermissionsAction={handleViewPermissionsAction}
            onViewUsersAction={handleViewUsersAction}
            onStatusAction={handleStatusAction}
            onPageChange={handleMuiPageChange}
            onRowsPerPageChange={handleMuiRowsPerPageChange}
          />
        </div>
      </div>

      <ConfirmDialog
        open={cannotInactivateOpen}
        title={RBAC_ROLE_DETAIL_TEXTS.cannotInactivateModalTitle}
        message={getRbacRoleCannotInactivateMessage(cannotInactivateRole)}
        cancelLabel={RBAC_ROLE_DETAIL_TEXTS.cancel}
        confirmLabel={RBAC_ROLE_DETAIL_TEXTS.cannotInactivateModalConfirm}
        onClose={closeCannotInactivateDialog}
        onConfirm={handleGoToReassignUsers}
        onExited={handleCannotInactivateDialogExited}
        titleId="rbac-roles-cannot-inactivate-title"
      />

      <RbacRoleConfirmDialog
        open={statusDialogOpen}
        onClose={closeStatusDialog}
        onExited={handleStatusDialogExited}
        onConfirm={handleUpdateRoleStatus}
        role={statusTarget}
        title={statusTargetActive ? '¿Inactivar este rol?' : '¿Activar este rol?'}
        body={
          statusTargetActive
            ? RBAC_ROLE_DETAIL_TEXTS.deactivateModalBody
            : RBAC_ROLE_DETAIL_TEXTS.activateModalBody
        }
        confirmLabel={statusTargetActive ? 'Inactivar rol' : 'Activar rol'}
        confirmColor={statusTargetActive ? UI_COLOR_DANGER_ACTION : UI_COLOR_PRIMARY}
        confirmHoverColor={
          statusTargetActive ? UI_COLOR_DANGER_ACTION_HOVER : UI_COLOR_PRIMARY_HOVER
        }
        submitting={updatingStatus}
        loadingLabel="Guardando..."
      />
    </>
  )
}
